import React from "react";
import styles from "./CalendaCalendar.module.css";
import { formatPhoneBR } from "../../utils/phone";
import { getSafeContactName, getSafePhone } from "../../utils/identity";
import {
  formatTimeBRT,
  formatPriceBRL,
  minutesFromGridStart,
  getDurationMinutes,
  getProfessionalColor,
  resolveStatusBadge,
  HOUR_HEIGHT_PX,
  MIN_EVENT_HEIGHT_PX,
  GRID_START_HOUR,
} from "./calendarHelpers";

/**
 * Card individual de agendamento, posicionado em absolute dentro de uma coluna
 * de dia. Recebe `column`/`colSpan`/`totalColumns` do algoritmo de overlap para
 * se distribuir lado a lado com eventos simultâneos, expandindo à direita
 * quando há espaço livre.
 *
 * A faixa horária é dinâmica: `gridStartHour` e `gridMinutes` vêm da grade
 * (derivados de businessHours ∪ agendamentos), garantindo que nenhum evento
 * fora do expediente seja escondido/clampado.
 */
const AppointmentCard = ({
  appointment,
  column = 0,
  colSpan = 1,
  totalColumns = 1,
  color,
  statusOptions = [],
  gridStartHour = GRID_START_HOUR,
  gridMinutes,
  onClick,
}) => {
  const startMin = minutesFromGridStart(appointment.startTime, gridStartHour);
  const duration = getDurationMinutes(appointment);

  // A faixa é construída para englobar todos os eventos — não clampamos mais o
  // início ao expediente (apenas evitamos posições negativas por segurança
  // numérica). Assim um agendamento às 07:00 ou 20:00 aparece na posição certa.
  const safeStart = Math.max(0, startMin);
  const rawHeight = (duration / 60) * HOUR_HEIGHT_PX;
  const top = (safeStart / 60) * HOUR_HEIGHT_PX;
  // Limita a altura ao fim da grade quando há gridMinutes definido.
  const maxHeight =
    typeof gridMinutes === "number"
      ? Math.max(MIN_EVENT_HEIGHT_PX, (gridMinutes / 60) * HOUR_HEIGHT_PX - top)
      : Infinity;
  const height = Math.min(Math.max(rawHeight, MIN_EVENT_HEIGHT_PX), maxHeight);

  const accent = color ?? getProfessionalColor(appointment.professionalId);
  const isCancelled = appointment.status === "cancelled";
  const badge = resolveStatusBadge(appointment.status, statusOptions);

  const startStr = formatTimeBRT(appointment.startTime);
  const endStr = formatTimeBRT(appointment.endTime);
  const priceStr = formatPriceBRL(appointment.service?.price);
  const profName = appointment.professional?.user?.name ?? "Sem profissional";
  const clientName = getSafeContactName(
    appointment.clientName,
    "Cliente sem nome",
  );
  const clientPhone = getSafePhone(appointment.clientPhone);

  const left = (column / totalColumns) * 100;
  const width = `calc(${(colSpan / totalColumns) * 100}% - 4px)`;

  const tooltip = [
    clientName,
    clientPhone ? formatPhoneBR(clientPhone) : null,
    appointment.service?.name,
    `${startStr} – ${endStr}`,
    profName,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`${styles.appointmentBlock}${isCancelled ? ` ${styles.appointmentCancelled}` : ""}`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `${left}%`,
        width,
        backgroundColor: accent,
      }}
      title={tooltip}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick(appointment);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(appointment);
        }
      }}
    >
      <span className={styles.appTime}>
        {startStr} – {endStr}
      </span>
      <span className={styles.appClient}>{clientName}</span>
      {appointment.service?.name && (
        <span className={styles.appService}>
          {appointment.service.name}
          {priceStr ? ` · ${priceStr}` : ""}
        </span>
      )}
      {badge && (
        <span className={`${styles.pill} ${styles[`pill_${badge.tone}`]}`}>
          {badge.label}
        </span>
      )}
    </div>
  );
};

export default AppointmentCard;
